import { Switch } from '@material-ui/core';
import React, { useState } from 'react';

import { ISmallBidderSmartFloors } from '~/src/dash/api/filters/bidders';
import {
  IInventorySmartFloorConfigUI,
  IInventorySmartFloorPayloadUI,
  inventorySmartFloorConfigCols,
} from '~/src/dash/apiV2/smartFloors/crud';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { spacing, spacingV2 } from '~/src/dash/components/layout/common';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { PillSwitcherV2 } from '~/src/dash/components/tabs/PillSwitcherV2';
import {
  borderRadius,
  Col,
  Cols,
  IndependentSelectV2,
  ISimpleOption,
  ISimpleOptionSelect,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  Text,
  TextInput,
  TooltipWithTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  SmartFloorsListContext,
  SmartFloorsLiveAdaptiveContext,
  // SmartFloorsLiveContext,
} from '~/src/dash/views/channels/SmartFloors/contexts';
import { smartFloorsListViewAdaptiveConfig } from '~/src/dash/views/channels/SmartFloors/SmartFloorsListView/config';
import { SmartFloorsListView } from '~/src/dash/views/channels/SmartFloors/SmartFloorsListView/index';
import { smartFloorsLiveAdaptiveConfig } from '~/src/dash/views/channels/SmartFloors/SmartFloorsLiveAdaptiveView/config';
import { SmartFloorsLiveAdaptiveContainer } from '~/src/dash/views/channels/SmartFloors/SmartFloorsLiveAdaptiveView/container';
// import { SmartFloorsLive } from '~/src/dash/views/channels/SmartFloors/SmartFloorsLiveView';
// import { smartFloorsLiveConfig } from '~/src/dash/views/channels/SmartFloors/SmartFloorsLiveView/config';

interface IProps {
  smartFloorConfigs: IInventorySmartFloorConfigUI[];
  selectedBidderIDForSmartFloorsChart: number;
  handleChangeSmartFloorConfig: (
    configIdx: number,
    key: inventorySmartFloorConfigCols,
    value: boolean | number | string,
    siteID: number,
    emptyOutSmartFloors: boolean,
    shouldUpdateSmartFloorParams: boolean,
  ) => void;
  connectedBidders: ISmallBidderSmartFloors[];
  siteId: number;
}

enum ESmartFloorSwitchType {
  Percent = '%',
  Dollar = '$',
}

const incrementTypeCategories: Array<ISimpleOption> = [
  { key: 'percent', value: ESmartFloorSwitchType.Percent, label: ESmartFloorSwitchType.Percent },
  { key: 'dollar', value: ESmartFloorSwitchType.Dollar, label: ESmartFloorSwitchType.Dollar },
];

const getOptions = (
  items: Array<ISimpleOption>,
  selectedValue: string,
  isOptionValid: (value: ISimpleOption) => boolean = () => false,
): Array<ISimpleOptionSelect> => {
  return items.map((item) => ({
    ...item,
    isSelected: item.value === selectedValue,
    isDisabled: !isOptionValid(item),
  }));
};

export const SiteSmartFloors = ({
  smartFloorConfigs,
  selectedBidderIDForSmartFloorsChart,
  handleChangeSmartFloorConfig,
  connectedBidders,
  siteId,
}: IProps): React.ReactNode => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { user } = useBaseStore();
  const readAuthorization = IS_AUTHORIZED(['read_bidder'], user.user); // eslint-disable-line i18next/no-literal-string
  const writeAuthorization = IS_AUTHORIZED(['write_bidder'], user.user); // eslint-disable-line i18next/no-literal-string

  if (!readAuthorization.isAuthorized) {
    return (
      <div>
        <ModelMissingPermission missingPermissions={readAuthorization.missingPermissions} />
      </div>
    );
  }

  const smartFloorConfig = smartFloorConfigs[0];
  let selectedSmartFloor = {} as IInventorySmartFloorPayloadUI;
  let selectedBidderName = '';

  // transform site and channel group IDs to name for table
  smartFloorConfig.InventorySmartFloors.forEach((smartFloor) => {
    // smartFloor.BidderName = channelMap[String(smartFloor.SiteID)];

    // selected smartFloorID for chart
    if (smartFloor.HeaderbidderID === selectedBidderIDForSmartFloorsChart) {
      selectedSmartFloor = smartFloor;
      selectedBidderName = smartFloor.BidderName ?? '';
    }

    // smartFloor.ChannelGroupNames = smartFloor.AssociatedChannelGroupIDs?.map((cgID) => {
    //   return channelAndGroupMaps.channelGroupMap[String(cgID)];
    // });
  });

  // only use testIncrementHighPercentage for now since both high and low % will be the same
  let testIncrementSwitchType = ESmartFloorSwitchType.Dollar;
  let testBidFloorSwitchType = ESmartFloorSwitchType.Dollar;
  if (smartFloorConfig.IsTestIncrementPercentage) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    testIncrementSwitchType = ESmartFloorSwitchType.Percent;
  }
  if (smartFloorConfig.IsBidFloorPercentage) {
    testBidFloorSwitchType = ESmartFloorSwitchType.Percent;
  }
  const handleSelectBidderOnChart = (bidderID: number, currVal: boolean): void => {
    handleChangeSmartFloorConfig(0, 'isBidderIDSelected', currVal, bidderID, false, false);
  };

  const handleUpdateBidFloorForSmartFloor = (bidderID: number, newValue: number): void => {
    handleChangeSmartFloorConfig(0, 'ControlBidFloor', newValue, bidderID, false, true);
  };

  // TODO: THIS IS INCORRECT FORMATTING OF ROWS AND COLS. WE NEED TO FIX THIS. DO NOT COPY THIS PATTERN.
  return (
    <Panels max vertical relative minWidthMain>
      <Panel basePadding largeHorizontalPadding enableSize>
        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }} smallVerticalPadding>
          <Col style={{ display: 'flex', alignItems: 'center' }}>
            <IconV2 name='shuffle' large />
          </Col>
          <Col>
            <Text small heavy tKey='TestingRange' />
          </Col>
          <Col
            displayFlex
            onClick={() => setIsCollapsed(!isCollapsed)}
            marginLeft={spacing.extraSmall}
          >
            <IconV2
              name='caret'
              large
              style={{ transform: isCollapsed ? 'none' : 'rotate(180deg)', cursor: 'pointer' }}
            />
          </Col>
        </Cols>
        {!isCollapsed && (
          <>
            <Rows
              basePadding
              style={{ background: publicaColors.veryLightGray, borderRadius: borderRadius.large }}
            >
              <Row noPadding marginBottom={spacing.small}>
                <Text extraSmall tKey='BiddersMessageSmartFloorTestingRangeParameters' />
              </Row>
              <Row noVerticalPadding>
                <Cols gap={spacingV2.medium} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='ABTestingTraffic' />
                    <Cols style={{ alignItems: 'center' }}>
                      <Col>
                        <Text
                          mini
                          style={{ backgroundColor: publicaColors.skyBlue }}
                          tKey='BiddersMessageSmartFloorsABTestingTraffic'
                        />
                      </Col>
                      <Col displayFlex style={{ marginLeft: spacing.extraSmall }}>
                        <TooltipWithTranslation tKey='BiddersTooltipSmartFloorsABTestingTraffic'>
                          <div>
                            <IconV2 small name='info' color={publicaColors.mediumGray} />
                          </div>
                        </TooltipWithTranslation>
                      </Col>
                    </Cols>
                  </Col>

                  <Col displayFlex>
                    <Switch
                      disabled={!writeAuthorization.isAuthorized}
                      checked={smartFloorConfig.IsABTestEnabled}
                      name='Active'
                      onChange={() => {
                        handleChangeSmartFloorConfig(
                          0,
                          'IsABTestEnabled',
                          !smartFloorConfig.IsABTestEnabled,
                          0,
                          false,
                          false,
                        );
                      }}
                    />
                    <Text
                      small
                      style={{ display: 'flex', alignItems: 'center' }}
                      tKey={smartFloorConfig.IsABTestEnabled ? 'Active' : 'Inactive'}
                    />
                  </Col>
                </Cols>
              </Row>

              {/* <Row extraSmallVerticalPadding>
                <Cols gap={spacingV2.extraLarge} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='Increment' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='BiddersMessageSmartFloorsIncrement'
                    />
                  </Col>

                  <Col>
                    <Cols wrap style={{ alignItems: 'center' }}>
                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Unit' />
                          </Col>
                          <Col>
                            <PillSwitcherV2
                              size='extraSmall'
                              options={getOptions(incrementTypeCategories, testIncrementSwitchType)}
                              backgroundColor={publicaColors.whiteOrchid}
                              onChange={(type: ESmartFloorSwitchType) => {
                                handleChangeSmartFloorConfig(
                                  0,
                                  'IsTestIncrementPercentage',
                                  type === ESmartFloorSwitchType.Percent,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Value' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.TestIncrement)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testIncrementSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'TestIncrement',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row> */}

              <Row extraSmallVerticalPadding>
                <Cols gap={spacingV2.extraLarge}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='FloorLimits' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='BiddersMessageSmartFloorsFloorLimits'
                    />
                  </Col>

                  <Col>
                    <Cols wrap gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='Unit' />
                          </Col>
                          <Col>
                            <PillSwitcherV2
                              size='extraSmall'
                              options={getOptions(incrementTypeCategories, testBidFloorSwitchType)}
                              backgroundColor={publicaColors.whiteOrchid}
                              onChange={(type: ESmartFloorSwitchType) => {
                                handleChangeSmartFloorConfig(
                                  0,
                                  'IsBidFloorPercentage',
                                  type === ESmartFloorSwitchType.Percent,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='MinValue' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.MinBidFloor)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testBidFloorSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'MinBidFloor',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>

                      <Col style={{ marginTop: spacing.small }}>
                        <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                          <Col>
                            <Text small bold tKey='MaxValue' />
                          </Col>
                          <Col>
                            <TextInput
                              type='number'
                              min={0}
                              initialValue={String(smartFloorConfig.MaxBidFloor)}
                              width='5rem'
                              inputSize='small'
                              step={0.5}
                              placeholder={`${testBidFloorSwitchType} inc`}
                              handleOnChange={(input) => {
                                let numInput = parseFloat(input);
                                if (isNaN(numInput)) {
                                  numInput = 0;
                                }
                                handleChangeSmartFloorConfig(
                                  0,
                                  'MaxBidFloor',
                                  numInput,
                                  0,
                                  false,
                                  true,
                                );
                              }}
                            />
                          </Col>
                        </Cols>
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
              <Row>
                <Cols gap={spacingV2.extraLarge}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='DefaultBaseFloor' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='DefaultBaseFloorTooltip'
                    />
                  </Col>

                  <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                    <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col>
                        <Text small bold tKey='Value' />
                      </Col>
                      <Col>
                        <TextInput
                          type='number'
                          min={0}
                          initialValue={String(smartFloorConfig.FallBackBaseFloor)}
                          width='5rem'
                          inputSize='small'
                          step={0.5}
                          placeholder={`${testBidFloorSwitchType} inc`}
                          handleOnChange={(input) => {
                            let numInput = parseFloat(input);
                            if (isNaN(numInput)) {
                              numInput = 0;
                            }
                            handleChangeSmartFloorConfig(
                              0,
                              'FallBackBaseFloor',
                              numInput,
                              0,
                              false,
                              true,
                            );
                          }}
                        />
                      </Col>
                      <Col paddingLeft={spacing.base}>
                        <Text
                          style={{
                            width: 'fit-content',
                            background: publicaColors.lighterYellow,
                            padding: `0 ${spacing.extraSmall}`,
                          }}
                          extraSmall
                          tKey='DefaultBaseFloorWarning'
                        />
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
              <Row extraSmallVerticalPadding>
                <Cols gap={spacingV2.extraLarge}>
                  <Col style={{ minWidth: '360px' }}>
                    <Text small bold tKey='ExcludeHeaderbidders' />
                    <Text
                      mini
                      style={{ backgroundColor: publicaColors.skyBlue, width: 'fit-content' }}
                      tKey='ExcludeHeaderbiddersTooltip'
                    />
                  </Col>
                  <Col style={{ marginRight: spacing.base, marginTop: spacing.small }}>
                    <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col>
                        {/* list of header bidders to exclude */}
                        <IndependentSelectV2
                          key='exclude-headerbidders-select'
                          selectKey=''
                          options={connectedBidders.map((bidder) => ({
                            id: bidder.ID,
                            label: bidder.Name,
                          }))}
                          labelTKey='HeaderBidderSelectToExclude'
                          selectedOptionIDs={
                            new Set(
                              smartFloorConfig.ExcludedHeaderbidderIDs?.split(',').filter(
                                Boolean,
                              ) ?? [],
                            )
                          }
                          selectedLimit={Infinity}
                          handleApplySelect={(selectedOptionIDs: Set<number | string>) => {
                            handleChangeSmartFloorConfig(
                              0,
                              'ExcludedHeaderbidderIDs',
                              Array.from(selectedOptionIDs).join(','),
                              0,
                              false,
                              true,
                            );
                          }}
                          disableSort={false}
                          disableSearch={false}
                        />
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
            </Rows>
          </>
        )}
      </Panel>

      <Panel largeHorizontalPadding noVerticalPadding enableSize>
        <SmartFloorsLiveAdaptiveContext.Provider
          value={{
            liveConfig: smartFloorsLiveAdaptiveConfig,
            selectedBidder: { ID: selectedBidderIDForSmartFloorsChart, Name: selectedBidderName },
            selectedSmartFloor,
          }}
        >
          <SmartFloorsLiveAdaptiveContainer siteId={siteId} />
        </SmartFloorsLiveAdaptiveContext.Provider>
      </Panel>

      <Panel basePadding largeHorizontalPadding enableSize>
        <SmartFloorsListContext.Provider
          value={{
            SmartFloors: {
              data: smartFloorConfig.InventorySmartFloors,
              cfg: smartFloorsListViewAdaptiveConfig,
              selectedBidderIDForSmartFloorsChart,
            },
            handlers: {
              handleSelectBidderOnChart,
              handleUpdateBidFloorForSmartFloor,
            },
            shouldAllowSortingTable: smartFloorConfig.shouldAllowSortingTable || false,
            readAuthorization,
            writeAuthorization,
          }}
        >
          <SmartFloorsListView />
        </SmartFloorsListContext.Provider>
      </Panel>
      <PanelBreak height={spacing.large} />
    </Panels>
  );
};
